import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnDestroy, OnInit, OnChanges, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
	selector: 'app-select-input',
	templateUrl: './select-input.component.html',
	providers: [
		{
			provide: [],
			useExisting: forwardRef(() => SelectInputComponent),
			multi: true,
		},
	],
})
export class SelectInputComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {
	@Input() label?: string;
	@Input() placeholder = '';
	@Input() disabled = false;
	@Input() configuration = false;
	@Input() loading = false;
	@Input() clearable = true;
	@Input() options = [];

 	@Output() selectionChange = new EventEmitter<unknown>();

	private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

	@ViewChild('ngSelect') ngSelectRef: NgSelectComponent;

	// null (not '') — ng-select treats '' as an unmatched selected value and
	// reacts by (a) showing the clear button instead of the placeholder, and
	// (b) firing its onChange to reconcile, which dirties the parent control
	// on init and triggers the red error border before any user interaction.
	public control: UntypedFormControl = new UntypedFormControl(null);
	public updateValue = (_: any) => {};
	public onTouched = () => {};
	public keyboardNav = false;

	constructor(public ngControl: NgControl, private el: ElementRef) {
		ngControl.valueAccessor = this;
	}

	private propagateChange(value: any): void {
		this.selectionChange.emit(value);

		if (this.updateValue) {
			return this.updateValue(value);
		}

		if (this.control) {
			this.control.setValue(value, {
				emitEvent: false
			});
		}
	}

	public ngOnInit(): void {
		if (this.disabled) {
			this.control.disable({ emitEvent: false });
		}

		this.control.valueChanges.pipe(
			takeUntil(this.componentDestroyed$),
		).subscribe((value) => {
			this.propagateChange(value);
		});
	}

	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (this.ngSelectRef?.isOpen) {
			this.ngSelectRef.close();
		}
	}

	public onOpen(): void {
		setTimeout(() => {
			const panel = this.el.nativeElement.querySelector('.ng-dropdown-panel-items');
			const selected = this.el.nativeElement.querySelector('.ng-option-selected');
			if (panel && selected) {
				const optionHeight = selected.offsetHeight;
				panel.scrollTop = Math.max(0, selected.offsetTop - optionHeight);
			}
		});
	}

	public onKeyDown(e: KeyboardEvent): void {
		if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
			this.keyboardNav = true;
		}
	}

	public onMouseMove(): void {
		if (this.keyboardNav) {
			this.keyboardNav = false;
		}
	}

	public trackByFn(index, item): void {
		return index;
	}

	public ngOnChanges(changes: any): void {
		if (changes.options) {
			// Update rendering or something
		}
	}

	public ngOnDestroy(): void {
		this.componentDestroyed$.next(true);
		this.componentDestroyed$.complete();
	}

	public writeValue(value: any): void {
		setTimeout(() => this.control.setValue(value, {
			emitEvent: false
		}));
	}

	public registerOnChange(fn: any): void {
		this.updateValue = fn;
	}

	public registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	public onBlur(): void {
		this.onTouched();
	}

	public setDisabledState(isDisabled: boolean) {
		// emitEvent: false — Angular forms calls setDisabledState during the
		// FormControlName setup. If enable/disable emits valueChanges here, our
		// propagateChange runs onChange against a control path that is not yet
		// attached, throwing "There is no FormControl instance attached".
		isDisabled
			? this.control.disable({ emitEvent: false })
			: this.control.enable({ emitEvent: false });
		this.disabled = isDisabled;
	}
}
